import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import {$applyNodeReplacement, DecoratorNode} from 'lexical';
import {ReactNode, Suspense} from 'react';

import {VideoComponent} from './VideoComponent';

export interface VideoPayload {
  src: string;
  altText: string;
  poster?: string;
  width?: number;
  height?: number;
  key?: NodeKey;
}

function convertVideoElement(domNode: Node): null | DOMConversionOutput {
  const video = domNode as HTMLVideoElement;
  if (!video.src) {
    return null;
  }
  const {src, width, height} = video;
  const node = $createVideoNode({
    altText: video.getAttribute('aria-label') ?? '',
    poster: video.getAttribute('poster') || undefined,
    src,
    width,
    height,
  });
  return {node};
}

export type SerializedVideoNode = Spread<
  {
    src: string;
    altText: string;
    poster?: string;
    width?: number;
    height?: number;
  },
  SerializedLexicalNode
>;

export class VideoNode extends DecoratorNode<ReactNode> {
  __src: string;
  __altText: string;
  __poster?: string;
  __width?: number;
  __height?: number;

  static getType(): string {
    return 'video';
  }

  static clone(node: VideoNode): VideoNode {
    return new VideoNode(
      node.__src,
      node.__altText,
      node.__width,
      node.__height,
      node.__poster,
      node.__key
    );
  }

  static importJSON(serializedNode: SerializedVideoNode): VideoNode {
    const {altText, height, width, src, poster} = serializedNode;
    const node = $createVideoNode({
      altText,
      height,
      poster,
      src,
      width,
    });
    return node;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('video');
    element.setAttribute('src', this.__src);
    if (this.__altText) {
      element.setAttribute('aria-label', this.__altText);
    }
    if (this.__poster) {
      element.setAttribute('poster', this.__poster);
    }
    element.setAttribute('preload', 'metadata');
    element.setAttribute('playsinline', 'true');
    if (this.__width) {
      element.setAttribute('width', this.__width.toString());
    }
    if (this.__height) {
      element.setAttribute('height', this.__height.toString());
    }
    element.setAttribute('controls', 'true');
    return {element};
  }

  static importDOM(): DOMConversionMap | null {
    return {
      video: () => ({
        conversion: convertVideoElement,
        priority: 0,
      }),
    };
  }

  constructor(
    src: string,
    altText: string,
    width?: number,
    height?: number,
    poster?: string,
    key?: NodeKey
  ) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__poster = poster;
    this.__width = width;
    this.__height = height;
  }

  exportJSON(): SerializedVideoNode {
    return {
      altText: this.getAltText(),
      height: this.__height,
      ...(this.__poster ? {poster: this.__poster} : {}),
      src: this.getSrc(),
      type: 'video',
      version: 1,
      width: this.__width,
    };
  }

  setSrcAndPoster(src: string, poster: string | undefined): void {
    const writable = this.getWritable();
    writable.__src = src;
    writable.__poster = poster;
  }

  setWidthAndHeight(width: number, height: number): void {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
  }

  // View

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    const theme = config.theme;
    const className = theme.video;
    if (className !== undefined) {
      span.className = className;
    }
    return span;
  }

  updateDOM(): false {
    return false;
  }

  getSrc(): string {
    return this.__src;
  }

  getAltText(): string {
    return this.__altText;
  }

  decorate() {
    return (
      <Suspense fallback={null}>
        <VideoComponent
          src={this.__src}
          poster={this.__poster}
          altText={this.__altText}
          width={this.__width}
          height={this.__height}
          nodeKey={this.getKey()}
        />
      </Suspense>
    );
  }
}

export function $createVideoNode({
  src,
  altText,
  poster,
  width,
  height,
  key,
}: VideoPayload): VideoNode {
  return $applyNodeReplacement(new VideoNode(src, altText, width, height, poster, key));
}

export function $isVideoNode(node: LexicalNode | null | undefined): node is VideoNode {
  return node instanceof VideoNode;
}
